import React, { useState } from "react";
import Table from "rc-table";
import moment from "moment";
import { Link } from "react-router-dom";
import { Button, Pagination, PopupModal, Toast } from "../../shared";
import { ROUTE_ENUM, TOAST_TYPES_ENUM } from "../../enums";
import {
  useCheckStock,
  useGenerateReport,
  useGetReportComponents,
  useGetReports,
  useResolveComponent,
  useRestoreComponent,
} from "../../hooks/report";
import axiosInstance from "../../axiosConfig";
import { Report, ReportComponent } from "../../types/ReportTypes";
import "./ReportsPage.scss";

type ReportsPageProps = {};

const STATUS_LABELS: { [key: string]: { label: string; className: string } } =
  {
    pending: { label: "Do sprawdzenia", className: "badge--neutral" },
    in_stock: { label: "Na stanie", className: "badge--in-stock" },
    missing: { label: "Brak - do zamówienia", className: "badge--missing" },
    not_found: { label: "Nie ma w Subiekcie", className: "badge--unresolved" },
    no_code: { label: "Brak kodu", className: "badge--unresolved" },
    error: { label: "Błąd sprawdzania", className: "badge--unresolved" },
  };

// What the user should do with a product from the skipped list
const SKIPPED_HINTS: { [key: string]: string } = {
  not_found:
    "Kodu nie ma w Subiekcie - sprawdź kod w ofercie Allegro albo dodaj produkt w Subiekcie.",
  no_code: "Oferta Allegro nie ma kodu (sygnatury) - uzupełnij go w ofercie.",
  error:
    "Subiekt nie odpowiada dla tego produktu od ponad 24 godzin - sprawdź stan w Subiekcie.",
  in_stock:
    "Produkt odnaleziony w Subiekcie - dodaj go do raportu albo usuń.",
  missing:
    "Produkt odnaleziony w Subiekcie, ale go brakuje - dodaj do raportu, jeśli nie został jeszcze zamówiony.",
};

// Shown instead of the quantity until Subiekt confirms the reservation
const RESERVATION_HINTS: { [key: string]: string } = {
  pending: "w trakcie",
  created: "-",
  failed: "błąd rezerwacji",
  cancelled: "zwolniona",
  cancel_failed: "błąd zwalniania",
};

const TYPE_LABELS: { [key: string]: string } = {
  manual: "Ręczny",
  auto: "Automatyczny",
};

const ReportsPage: React.FC<ReportsPageProps> = () => {
  const [page, setPage] = useState(1);
  const [selectedReport, setSelectedReport] = useState<Report | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [hideEmpty, setHideEmpty] = useState(false);
  // Products in stock need no order, so only the missing ones are shown unless asked
  const [showInStock, setShowInStock] = useState(false);
  const [componentToRemove, setComponentToRemove] =
    useState<ReportComponent | null>(null);
  const [isRemoveOpen, setIsRemoveOpen] = useState(false);
  const { data, isLoading } = useGetReports(page, hideEmpty);
  const { data: pendingComponents, isFetching: isFetchingPending } =
    useGetReportComponents(null);
  const { data: reportComponents, isFetching: isFetchingReport } =
    useGetReportComponents(selectedReport?.id ?? null, !!selectedReport);

  const { mutate: checkStock, isPending: isChecking } = useCheckStock();

  const { mutate: resolveComponent, isPending: isResolving } =
    useResolveComponent({ onSuccess: () => setIsRemoveOpen(false) });

  const { mutate: restoreComponent, isPending: isRestoring } =
    useRestoreComponent();

  const hasPendingComponents = (pendingComponents?.data || []).length > 0;
  const skippedComponents = pendingComponents?.skipped || [];
  const filterInStock = (components: ReportComponent[]) =>
    showInStock
      ? components
      : components.filter((component) => component.status !== "in_stock");
  const visiblePendingComponents = filterInStock(pendingComponents?.data || []);
  const hiddenPendingCount =
    (pendingComponents?.data || []).length - visiblePendingComponents.length;
  const visibleReportComponents = filterInStock(reportComponents?.data || []);
  const hiddenReportCount =
    (reportComponents?.data || []).length - visibleReportComponents.length;

  const renderShowInStock = (hiddenCount: number) => (
    <label className="reports-page__filter">
      <input
        type="checkbox"
        checked={showInStock}
        onChange={(e) => setShowInStock(e.target.checked)}
      />
      Pokaż produkty na stanie
      {!showInStock && hiddenCount > 0 && ` (ukryte: ${hiddenCount})`}
    </label>
  );
  const connector = pendingComponents?.connector;
  const waitingCodes = connector?.enabled ? connector.waitingCodes : 0;

  const openDetails = (report: Report) => {
    setSelectedReport(report);
    setIsDetailsOpen(true);
  };
  const { mutate: generateReport, isPending: isGenerating } =
    useGenerateReport({
      onSuccess: (response) => {
        setPage(1);
        if (response?.report) {
          openDetails(response.report);
        }
      },
    });

  const formatDate = (date: string | null) =>
    date ? moment(date).local().format("DD.MM.YYYY, HH:mm") : "-";

  const formatQuantity = (quantity: number | null) =>
    quantity === undefined || quantity === null
      ? "-"
      : Number(quantity).toLocaleString("pl-PL", { maximumFractionDigits: 3 });

  // Two files with the same products: CSV for Motorro and .txt with semicolons
  const handleDownload = async (report: Report, format: "csv" | "txt") => {
    try {
      const response = await axiosInstance.get(
        `/reports/${report.id}/${format}`,
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `raport-${report.id}-${moment(report.createdAt)
        .local()
        .format("YYYY-MM-DD-HHmm")}.${format}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      Toast({
        message: "Błąd podczas pobierania pliku.",
        type: TOAST_TYPES_ENUM.ERROR,
        id: "download-report",
      });
    }
  };

  // Stocks come from the connector on the computer with Subiekt - nothing gets checked without it
  const renderConnectorAlert = () => {
    if (!connector?.enabled) {
      return null;
    }

    let message = "";
    if (!connector.lastSeenAt) {
      message = "Konektor Subiekta jeszcze się nie zgłosił";
    } else if (!connector.online) {
      message = `Konektor Subiekta nie zgłasza się od ${formatDate(
        connector.lastSeenAt
      )} - sprawdź, czy komputer z Subiektem jest włączony i ma dostęp do internetu`;
    } else if (connector.sferaConnected === false) {
      message = `Konektor działa, ale nie ma połączenia z Subiektem${
        connector.lastError ? ` (${connector.lastError})` : ""
      }`;
    }

    return message ? (
      <div className="reports-page__alert">
        {message}. Do tego czasu stany produktów nie są sprawdzane.{" "}
        <Link to={ROUTE_ENUM.TASKS}>Zobacz zadania konektora</Link>
      </div>
    ) : (
      <p className="reports-page__hint reports-page__hint--ok">
        Konektor Subiekta połączony · ostatni kontakt{" "}
        {formatDate(connector.lastSeenAt)}
        {connector.version ? ` · wersja ${connector.version}` : ""} ·{" "}
        <Link to={ROUTE_ENUM.TASKS}>zadania konektora</Link>
      </p>
    );
  };

  const renderStatus = (record: ReportComponent) => {
    if (record.checking) {
      return <span className="badge badge--neutral">Sprawdzanie...</span>;
    }

    const status = STATUS_LABELS[record.status];
    return (
      <span className={`badge ${status?.className || ""}`}>
        {status?.label || record.status}
      </span>
    );
  };

  const renderProduct = (record: ReportComponent) => (
    <div className="report-product">
      <div>{record.subiektName || record.offerName || "-"}</div>
      <div className="report-product__code">
        {record.code || "brak kodu"}
        {record.subiektSymbol ? ` · ${record.subiektSymbol}` : ""}
      </div>
      {record.status === "error" && record.message && (
        <div className="report-product__message">{record.message}</div>
      )}
    </div>
  );

  const renderOrder = (record: ReportComponent) => (
    <div className="report-product">
      <div>{record.buyerLogin || record.orderId || "-"}</div>
      <div className="report-product__code">
        {formatDate(record.orderedAt)}
      </div>
    </div>
  );

  const renderMissing = (record: ReportComponent) =>
    record.status === "missing" ? (
      <span className="report-missing">
        {formatQuantity(record.missingQuantity)}
      </span>
    ) : (
      "-"
    );

  const renderReserved = (record: ReportComponent) => {
    if (!record.reservationStatus) {
      return "-";
    }

    const isReserved =
      record.reservationStatus === "created" && record.reservedQuantity !== null;

    return (
      <div className="report-product">
        <div>
          {isReserved
            ? formatQuantity(record.reservedQuantity)
            : RESERVATION_HINTS[record.reservationStatus]}
        </div>
        {record.reservationNumber && (
          <div className="report-product__code">{record.reservationNumber}</div>
        )}
      </div>
    );
  };

  const renderActions = (record: Report) => (
    <div className="report-actions">
      <Button
        label="Szczegóły"
        onClick={() => openDetails(record)}
        styleType="stroke"
        size="small"
      />
      <Button
        label="Pobierz CSV"
        onClick={() => handleDownload(record, "csv")}
        styleType="primary"
        size="small"
        disabled={record.csvRowsCount === 0}
      />
      <Button
        label="Pobierz TXT"
        onClick={() => handleDownload(record, "txt")}
        styleType="primary"
        size="small"
        disabled={record.csvRowsCount === 0}
      />
    </div>
  );

  const reportColumns = [
    {
      title: "DATA",
      key: "createdAt",
      render: (_: any, record: Report) => formatDate(record.createdAt),
    },
    {
      title: "TYP",
      key: "type",
      render: (_: any, record: Report) =>
        TYPE_LABELS[record.type] || record.type,
    },
    {
      title: "SPRAWDZONE",
      key: "componentsCount",
      render: (_: any, record: Report) => record.componentsCount,
    },
    {
      title: "NA STANIE",
      key: "inStockCount",
      render: (_: any, record: Report) => record.inStockCount,
    },
    {
      title: "BRAKI",
      key: "missingCount",
      render: (_: any, record: Report) => record.missingCount,
    },
    {
      title: "",
      key: "actions",
      className: "reports-table__actions-cell",
      render: (_: any, record: Report) => renderActions(record),
    },
  ];

  const mobileReportColumns = [
    {
      title: "Raporty",
      key: "mobile",
      render: (_: any, record: Report) => (
        <div className="mobile-record">
          <div className="mobile-record__date">
            {formatDate(record.createdAt)} ·{" "}
            {TYPE_LABELS[record.type] || record.type}
          </div>
          <div className="mobile-record__user">
            Na stanie: {record.inStockCount} · Braki: {record.missingCount}
          </div>
          {renderActions(record)}
        </div>
      ),
    },
  ];

  const componentColumns = [
    {
      title: "PRODUKT",
      key: "product",
      render: (_: any, record: ReportComponent) => renderProduct(record),
    },
    {
      title: "ZAMÓWIENIE",
      key: "order",
      render: (_: any, record: ReportComponent) => renderOrder(record),
    },
    {
      title: "POTRZEBA",
      key: "quantity",
      render: (_: any, record: ReportComponent) =>
        formatQuantity(record.quantity),
    },
    {
      title: "DOSTĘPNE",
      key: "stockQuantity",
      render: (_: any, record: ReportComponent) =>
        formatQuantity(record.stockQuantity),
    },
    {
      title: "BRAKUJE",
      key: "missingQuantity",
      render: (_: any, record: ReportComponent) => renderMissing(record),
    },
    {
      title: "ZAREZERWOWANO",
      key: "reservedQuantity",
      render: (_: any, record: ReportComponent) => renderReserved(record),
    },
    {
      title: "STATUS",
      key: "status",
      render: (_: any, record: ReportComponent) => renderStatus(record),
    },
  ];

  const renderSkippedReason = (record: ReportComponent) => (
    <div className="report-product">
      {renderStatus(record)}
      <div className="report-product__code">
        {SKIPPED_HINTS[record.status]}
      </div>
    </div>
  );

  const renderRemoveButton = (record: ReportComponent, disabled: boolean) => (
    <Button
      label="Usuń"
      onClick={() => {
        setComponentToRemove(record);
        setIsRemoveOpen(true);
      }}
      styleType="stroke"
      size="small"
      disabled={disabled}
    />
  );

  const renderSkippedActions = (record: ReportComponent) => {
    const disabled = isResolving || isRestoring || isChecking || isGenerating;
    return (
      <div className="report-actions">
        {(record.status === "in_stock" || record.status === "missing") && (
          <Button
            label="Dodaj do raportu"
            onClick={() => restoreComponent(record.id)}
            styleType="primary"
            size="small"
            disabled={disabled}
          />
        )}
        {renderRemoveButton(record, disabled)}
      </div>
    );
  };

  // Products with a check error can be removed when they were handled manually
  const pendingColumns = [
    ...componentColumns,
    {
      title: "",
      key: "actions",
      className: "reports-table__actions-cell",
      render: (_: any, record: ReportComponent) =>
        record.status === "error"
          ? (
            <div className="report-actions">
              {renderRemoveButton(
                record,
                isResolving || isChecking || isGenerating
              )}
            </div>
          )
          : null,
    },
  ];

  const skippedColumns = [
    componentColumns[0],
    componentColumns[1],
    componentColumns[2],
    componentColumns[3],
    componentColumns[4],
    {
      title: "POWÓD",
      key: "reason",
      render: (_: any, record: ReportComponent) => renderSkippedReason(record),
    },
    {
      title: "",
      key: "actions",
      className: "reports-table__actions-cell",
      render: (_: any, record: ReportComponent) => renderSkippedActions(record),
    },
  ];

  const mobileSkippedColumns = [
    {
      title: "Produkty",
      key: "mobile",
      render: (_: any, record: ReportComponent) => (
        <div className="mobile-record">
          {renderProduct(record)}
          <div className="mobile-record__user">
            Potrzeba: {formatQuantity(record.quantity)} · Dostępne:{" "}
            {formatQuantity(record.stockQuantity)}
            {record.status === "missing" &&
              ` · Brakuje: ${formatQuantity(record.missingQuantity)}`}
          </div>
          {renderSkippedReason(record)}
          {renderSkippedActions(record)}
        </div>
      ),
    },
  ];

  const mobileComponentColumns = [
    {
      title: "Produkty",
      key: "mobile",
      render: (_: any, record: ReportComponent) => (
        <div className="mobile-record">
          {renderProduct(record)}
          <div className="mobile-record__user">
            Potrzeba: {formatQuantity(record.quantity)} · Dostępne:{" "}
            {formatQuantity(record.stockQuantity)}
            {record.status === "missing" &&
              ` · Brakuje: ${formatQuantity(record.missingQuantity)}`}
            {record.reservationStatus === "created" &&
              record.reservedQuantity !== null &&
              ` · Zarezerwowano: ${formatQuantity(record.reservedQuantity)}`}
          </div>
          {renderStatus(record)}
        </div>
      ),
    },
  ];

  const mobilePendingColumns = [
    {
      title: "Produkty",
      key: "mobile",
      render: (_: any, record: ReportComponent) => (
        <div className="mobile-record">
          {mobileComponentColumns[0].render(_, record)}
          {record.status === "error" &&
            renderRemoveButton(
              record,
              isResolving || isChecking || isGenerating
            )}
        </div>
      ),
    },
  ];

  if (isLoading) {
    return <div className="loading">Ładowanie...</div>;
  }

  const renderComponentsTables = (
    components: ReportComponent[],
    emptyText: string,
    columns: any[] = componentColumns,
    mobileColumns: any[] = mobileComponentColumns
  ) => (
    <>
      <Table
        rowKey="id"
        className="prices-table reports-table desktop-table"
        data={components}
        columns={columns}
        emptyText={emptyText}
      />

      <Table
        rowKey="id"
        className="prices-table reports-table mobile-table"
        data={components}
        columns={mobileColumns}
        showHeader={false}
        emptyText={emptyText}
      />
    </>
  );

  return (
    <div className="prices-page reports-page">
      <div className="prices-page__header">
        <h1>Raporty</h1>
        <Button
          label={isGenerating ? "Generowanie..." : "Generuj raport"}
          onClick={() => generateReport()}
          styleType="primary"
          disabled={isGenerating || isChecking || !hasPendingComponents}
        />
      </div>

      <label className="reports-page__filter">
        <input
          type="checkbox"
          checked={hideEmpty}
          onChange={(e) => {
            setHideEmpty(e.target.checked);
            setPage(1);
          }}
        />
        Ukryj puste
      </label>

      <Table
        rowKey="id"
        className="prices-table reports-table desktop-table"
        data={data?.data || []}
        columns={reportColumns}
        emptyText="Brak raportów do wyświetlenia"
      />

      <Table
        rowKey="id"
        className="prices-table reports-table mobile-table"
        data={data?.data || []}
        columns={mobileReportColumns}
        showHeader={false}
        emptyText="Brak raportów do wyświetlenia"
      />

      {data?.meta && data?.meta?.lastPage > 1 && (
        <Pagination
          totalPages={data?.meta?.lastPage}
          currentPage={data?.meta?.currentPage || page}
          onPageChange={(newPage: number) => setPage(newPage)}
        />
      )}

      <div className="reports-page__section">
        <h2>Oczekujące na następny raport</h2>
        <Button
          label={isChecking ? "Sprawdzanie..." : "Sprawdź stany"}
          onClick={() => checkStock()}
          styleType="stroke"
          size="small"
          disabled={isChecking || isGenerating}
        />
      </div>
      <p className="reports-page__hint">
        Produkty ze sprzedanych zamówień, które nie trafiły jeszcze do żadnego
        raportu. Pozycje z błędem są ponawiane automatycznie przez 24 godziny,
        potem trafiają do pominiętych. Jeśli taka pozycja została już zamówiona
        ręcznie, usuń ją, żeby nie trafiła do raportu.
        {waitingCodes > 0 &&
          ` Trwa sprawdzanie stanów w Subiekcie (produkty: ${waitingCodes}) - lista odświeży się sama.`}
      </p>
      {renderConnectorAlert()}
      {renderShowInStock(hiddenPendingCount)}

      {renderComponentsTables(
        visiblePendingComponents,
        isFetchingPending
          ? "Ładowanie..."
          : hiddenPendingCount > 0
            ? "Brak brakujących produktów - wszystkie oczekujące są na stanie"
            : "Brak produktów oczekujących na raport",
        pendingColumns,
        mobilePendingColumns
      )}

      <div className="reports-page__section">
        <h2>Pominięte</h2>
        <Button
          label={isChecking ? "Sprawdzanie..." : "Sprawdź ponownie"}
          onClick={() => checkStock()}
          styleType="stroke"
          size="small"
          disabled={isChecking || isGenerating || !skippedComponents.length}
        />
      </div>
      <p className="reports-page__hint">
        Produkty, których nie udało się sprawdzić automatycznie. Nigdy nie
        wracają same do raportu ani do pliku CSV - nawet gdy „Sprawdź ponownie”
        (albo codzienny raport) znajdzie je w Subiekcie. Wtedy kliknij „Dodaj
        do raportu”, a jeśli produkt został już zamówiony lub wyjaśniony
        ręcznie - „Usuń”.
      </p>

      <Table
        rowKey="id"
        className="prices-table reports-table desktop-table"
        data={skippedComponents}
        columns={skippedColumns}
        emptyText={isFetchingPending ? "Ładowanie..." : "Brak pominiętych produktów"}
      />

      <Table
        rowKey="id"
        className="prices-table reports-table mobile-table"
        data={skippedComponents}
        columns={mobileSkippedColumns}
        showHeader={false}
        emptyText={isFetchingPending ? "Ładowanie..." : "Brak pominiętych produktów"}
      />

      <PopupModal
        isOpen={isDetailsOpen}
        onClose={() => setIsDetailsOpen(false)}
        className="report-details-modal"
      >
        {selectedReport && (
          <>
            <div className="popup-modal__header">
              Raport z {formatDate(selectedReport.createdAt)}
            </div>
            <div className="report-details-modal__summary">
              {TYPE_LABELS[selectedReport.type] || selectedReport.type} · Na
              stanie: {selectedReport.inStockCount} · Braki:{" "}
              {selectedReport.missingCount}
            </div>
            {renderShowInStock(hiddenReportCount)}
            <div className="report-details-modal__content">
              {renderComponentsTables(
                visibleReportComponents,
                isFetchingReport
                  ? "Ładowanie..."
                  : hiddenReportCount > 0
                    ? "Ten raport nie zawiera brakujących produktów"
                    : "Ten raport nie zawiera żadnych produktów"
              )}
            </div>
            <div className="popup-modal__buttons">
              <Button
                label="Zamknij"
                onClick={() => setIsDetailsOpen(false)}
                styleType="stroke"
              />
              <Button
                label="Pobierz CSV"
                onClick={() => handleDownload(selectedReport, "csv")}
                styleType="primary"
                disabled={selectedReport.csvRowsCount === 0}
              />
              <Button
                label="Pobierz TXT"
                onClick={() => handleDownload(selectedReport, "txt")}
                styleType="primary"
                disabled={selectedReport.csvRowsCount === 0}
              />
            </div>
          </>
        )}
      </PopupModal>

      <PopupModal isOpen={isRemoveOpen} onClose={() => setIsRemoveOpen(false)}>
        {componentToRemove && (
          <>
            <div className="popup-modal__header">Usunąć produkt?</div>
            <div className="report-remove-modal__text">
              <strong>
                {componentToRemove.subiektName ||
                  componentToRemove.offerName ||
                  componentToRemove.code ||
                  "Produkt"}
              </strong>
              {componentToRemove.code ? ` (${componentToRemove.code})` : ""} z
              zamówienia{" "}
              {componentToRemove.buyerLogin || componentToRemove.orderId || "-"}{" "}
              zniknie z listy, nie trafi do żadnego raportu i nie będzie już
              sprawdzany. Tej operacji nie można cofnąć.
            </div>
            <div className="popup-modal__buttons">
              <Button
                label="Anuluj"
                onClick={() => setIsRemoveOpen(false)}
                styleType="stroke"
                disabled={isResolving}
              />
              <Button
                label={isResolving ? "Usuwanie..." : "Usuń"}
                onClick={() => resolveComponent(componentToRemove.id)}
                styleType="primary"
                disabled={isResolving}
              />
            </div>
          </>
        )}
      </PopupModal>
    </div>
  );
};

export default ReportsPage;
